import { RefusalReason, type ResourceId } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * The fight's wording on the message line (P2.1-T4): which sentence a refusal, a weakened ability and a death read
 * as. The sentences are the language files' (`fight.*` in `odyssey-lang`).
 */

/**
 * The sentence for an act the server refused (ADR-074 decision 3's reasons).
 *
 * @param reason The reason code the `refused` event carries.
 * @param ability The ability's name, or null when the page's rules do not hold its number.
 * @returns The sentence.
 * @example
 * refusalLine(RefusalReason.Cooldown, "Strike");
 * // "Strike is not ready yet."
 */
export function refusalLine(reason: number, ability: string | null): string {
  const name = ability ?? say("fight.unknownAbility");
  switch (reason) {
    case RefusalReason.Dead:
      return say("fight.refused.dead");
    case RefusalReason.Stunned:
      return say("fight.refused.stunned");
    case RefusalReason.Bar:
      return say("fight.refused.bar", { ability: name });
    case RefusalReason.Cooldown:
      return say("fight.refused.cooldown", { ability: name });
    case RefusalReason.Cost:
      return say("fight.refused.cost", { ability: ability ?? say("fight.thatAbility") });
    case RefusalReason.Target:
      return say("fight.refused.target", { ability: name });
    case RefusalReason.View:
      return say("fight.refused.view");
    case RefusalReason.Range:
      return say("fight.refused.range");
    case RefusalReason.Sight:
      return say("fight.refused.sight");
    case RefusalReason.Peace:
      return say("fight.refused.peace");
    case RefusalReason.Busy:
      return say("fight.refused.busy");
    case RefusalReason.Passage:
      return say("fight.refused.passage");
    case RefusalReason.TargetPassage:
      return say("fight.refused.targetPassage");
    case RefusalReason.Held:
      return say("fight.refused.held");
    case RefusalReason.TargetHeld:
      return say("fight.refused.targetHeld");
    default:
      return say("fight.refused.other", { ability: name });
  }
}

/**
 * A resource's name as a sentence and a cost print it.
 *
 * @param resource The resource.
 * @returns The name.
 * @example
 * resourceName("stamina");
 * // "Stamina"
 */
export function resourceName(resource: ResourceId): string {
  return say(`fight.resources.${resource}`);
}

/**
 * The sentence for a basic blow the map server swung unpaid (ADR-117 decision 9): the blow went out, took nothing and
 * deals less, which the `used` event's `Unpaid` bit says and the page never works out for itself.
 * The author approved this sentence as written, so it is not one of the drafts.
 *
 * @param resource The resource the blow's cost is in, or null when the page's rules give the blow no cost.
 * @param ability The blow's name, or null when the page's rules do not hold its number.
 * @returns The sentence.
 * @example
 * weakLine("stamina", "Strike");
 * // "Not enough Stamina: Strike is weakened."
 */
export function weakLine(resource: ResourceId | null, ability: string | null): string {
  const name = ability ?? say("fight.unknownAbility");
  return resource === null ? say("fight.weakened", { ability: name }) : say("fight.weakenedFor", { resource: resourceName(resource), ability: name });
}

/**
 * The line naming who or what killed the player, and with what (question 5): the chat's death line with the
 * killer's first name, and the death screen's cause with its full name (ADR-068's amendment "chat lines show the
 * first name", item 5).
 *
 * @param killer The killer's name as its reader shows it, or null when the event names none or the page holds no name.
 * @param ability The killing ability's name, or null when the event names none or the page's rules do not hold it.
 * @returns The sentence.
 * @example
 * deathLine("Ada", "Strike");
 * // "Ada killed you with Strike."
 * deathLine("Ada Valrathen", "Strike");
 * // "Ada Valrathen killed you with Strike."
 */
export function deathLine(killer: string | null, ability: string | null): string {
  if (killer !== null) {
    return ability === null ? say("fight.death.killer", { killer }) : say("fight.death.killerAbility", { killer, ability });
  }
  return ability === null ? say("fight.death.none") : say("fight.death.ability", { ability });
}
